import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { mixed, object, string } from 'yup'
import { Button } from '@mui/material'
import type { Auth0UserDTO, OrganisationDTO } from '@api/fastAPI.schemas'
import {
  getGetOrganisationsQueryKey,
  getGetUnassignedAuth0UsersQueryKey,
  getGetUsersQueryKey,
  useCreateUser,
  useGetUnassignedAuth0Users,
} from '@api/user-management/user-management'
import { useQueryClient } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { DialogBase } from '@ui/feedback'
import { Form2 } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import Auth0UserField from './components/Auth0UserField'
import UserFields from './components/UserFields'
import type { FormValues } from './types'

interface Props {
  open: boolean
  onClose: () => void
  onCreateAuth0User: () => void
  organisations: OrganisationDTO[]
}

const FORM_ID = 'create-user-form'

const defaultValues: FormValues = {
  selectedUser: null,
  name: '',
  organisation: null,
  isAdmin: false,
}

const validationSchema = object({
  selectedUser: mixed()
    .nullable()
    .required('Ein Auth0-Benutzer muss ausgewählt werden')
    .test('has-email', 'Der ausgewählte Nutzer hat keine E-Mail-Adresse', value =>
      Boolean((value as Auth0UserDTO | null)?.email),
    ),
  name: string().required('Name ist erforderlich'),
  // Mandatory: the backend never creates an organisation for a new user, so
  // one has to be picked here.
  organisation: mixed().nullable().required('Organisation ist erforderlich'),
})

const CreateUserDialog = ({
  open,
  onClose,
  onCreateAuth0User,
  organisations,
}: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { data: unassignedUsers, isLoading: isLoadingUnassigned } = useGetUnassignedAuth0Users<
    Auth0UserDTO[],
    AxiosError
  >({
    query: {
      enabled: open,
      select: data => data.users,
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Auth0-Benutzer'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutate: createUser, isLoading: isCreatingUser } = useCreateUser<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Nutzer erstellen',
        description: 'Registriert einen Benutzer in der gewählten Organisation',
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(getGetUsersQueryKey()),
          queryClient.invalidateQueries(getGetOrganisationsQueryKey()),
          queryClient.invalidateQueries(getGetUnassignedAuth0UsersQueryKey()),
        ])
        enqueueSnackbar('Nutzer erfolgreich erstellt', { variant: 'success' })
        onClose()
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Erstellen des Nutzers'), {
          variant: 'error',
        })
      },
    },
  })

  const handleSubmit = (values: FormValues) => {
    if (!values.selectedUser?.email || !values.organisation) return

    createUser({
      data: {
        auth0_id: values.selectedUser.id,
        email: values.selectedUser.email,
        name: values.name.trim(),
        organisation_guid: values.organisation.guid,
        is_admin: values.isAdmin,
      },
    })
  }

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title="Nutzer erstellen"
      dialogProps={{ maxWidth: 'sm', fullWidth: true }}
    >
      <Form2<FormValues>
        id={FORM_ID}
        defaultValues={defaultValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Auth0UserField users={unassignedUsers || []} loading={isLoadingUnassigned} />
          <Box>
            <Button size="small" onClick={onCreateAuth0User} data-cy="btn-new-auth0-user">
              Auth0 Nutzer erstellen
            </Button>
          </Box>
          <UserFields organisations={organisations} />
        </Box>
      </Form2>
      <>
        <Button size="small" onClick={onClose}>
          Abbrechen
        </Button>
        <SaveButton
          size="small"
          loading={isCreatingUser}
          form={FORM_ID}
          type="submit"
          data-cy="btn-save-user"
        >
          Erstellen
        </SaveButton>
      </>
    </DialogBase>
  )
}

export default CreateUserDialog
